import React, { useState, useEffect } from 'react';
import { Box, Select, VStack, HStack, useColorMode, Text, Flex } from '@chakra-ui/react';
import { ThemedFlex } from '../../theme/ThemedFlex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';

export const BitcoinMarketHoursTile = ({ animate }) => {
  const { colorMode } = useColorMode();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedTimezone, setSelectedTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  
  const titleColor = colorMode === "dark" ? "white.500" : "primary.700";
  const subtitleColor = colorMode === "dark" ? "gray.700" : "gray.600";

  // Bitcoin trading sessions in UTC
  // All sessions run 6am-12am midnight in their local time zones
  // Sydney: 6am-12am AEDT = 19:00-13:00 UTC (UTC+11)
  // Tokyo: 6am-12am JST = 21:00-15:00 UTC (UTC+9)
  // Dubai: 6am-12am GST = 02:00-20:00 UTC (UTC+4)
  // London: 6am-12am GMT = 06:00-00:00 UTC (UTC+0)
  // New York: 6am-12am EST = 11:00-05:00 UTC (UTC-5)

  const sessionsUTC = [
    {
      name: 'Sydney',
      utcStart: 20,  // 6am AEDT
      utcEnd: 14,    // 12am AEDT
      color: '#4A90E2'
    },
    {
      name: 'Tokyo', 
      utcStart: 21,  // 6am JST
      utcEnd: 15,    // 12am JST
      color: '#E24A4A'
    },
    {
      name: 'Dubai',
      utcStart: 2,   // 6am GST
      utcEnd: 20,    // 12am GST
      color: '#C77DFF'  
    },
    {
      name: 'London',
      utcStart: 5,   // 6am GMT
      utcEnd: 23,     // 12am GMT
      color: '#4AE290'
    },
    {
      name: 'New York',
      utcStart: 10,  // 6am EST
      utcEnd: 4,     // 12am EST
      color: '#E2904A'
    }
  ];

  // Get UTC offset for a timezone
  const getTimezoneOffset = (timezone) => {
    const now = new Date();
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    return Math.round((tzDate - utcDate) / (1000 * 60 * 60));
  };

  // Convert UTC session times to selected timezone
  const sessions = sessionsUTC.map(session => {
    const offset = getTimezoneOffset(selectedTimezone);
    
    let start = (session.utcStart + offset) % 24;
    if (start < 0) start += 24;
    
    let end = (session.utcEnd + offset) % 24;
    if (end < 0) end += 24;
    
    return {
      ...session,
      start,
      end
    };
  });

  // Common timezones for the selector
  const timezones = [
    { value: 'UTC', label: 'UTC (GMT+0)' },
    { value: 'America/New_York', label: 'New York (GMT-5)' },
    { value: 'America/Chicago', label: 'Chicago (GMT-6)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8)' },
    { value: 'America/Sao_Paulo', label: 'São Paulo (GMT-3)' },
    { value: 'Europe/London', label: 'London (GMT+0)' },
    { value: 'Europe/Paris', label: 'Paris (GMT+1)' },
    { value: 'Europe/Moscow', label: 'Moscow (GMT+3)' },
    { value: 'Asia/Dubai', label: 'Dubai (GMT+4)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (GMT+8)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9)' },
    { value: 'Asia/Singapore', label: 'Singapore (GMT+8)' },
    { value: 'Australia/Sydney', label: 'Sydney (GMT+11)' },
    { value: 'Pacific/Auckland', label: 'Auckland (GMT+13)' }
  ];

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Convert time to selected timezone
  const getTimeInTimezone = (date, timezone) => {
    return new Date(date.toLocaleString('en-US', { timeZone: timezone }));
  };

  // Check if session is active
  const isSessionActive = (session, currentHour) => {
    if (session.start < session.end) {
      // Session within same day
      return currentHour >= session.start && currentHour < session.end;
    } else {
      // Session crosses midnight
      return currentHour >= session.start || currentHour < session.end;
    }
  };


  const timeInSelectedZone = getTimeInTimezone(currentTime, selectedTimezone);
  const currentHour = timeInSelectedZone.getHours();
  const currentMinute = timeInSelectedZone.getMinutes();

  return (
    <ThemedFlex
      direction="column"
      mb="12px"
      py="18px"
      px="20px"
      flex={1}
      borderRadius={3}
      className={animate ? 'gray-border-custom gray-border' : ''}
    >
      {/* Header matching DataTableHeader styling */}
      <Flex justify="space-between" mb={4}>
        <Box>
          <Text
            fontSize="22px"
            fontWeight="700"
            color={titleColor}
            lineHeight="none"
            mb="7px"
          >
            Sessions Clock
          </Text>
          <Text
            fontSize="14px"
            fontWeight="400"
            color={subtitleColor}
            lineHeight="shorter"
          >
            Counting 6am-12am as a Session
            {/* {timeInSelectedZone.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })} */}
          </Text>
        </Box>
        <Box>
          <FontAwesomeIcon icon={faClock} fontSize="20px" color={subtitleColor} />
        </Box>
      </Flex>

      <VStack spacing={3} align="stretch" mt={0}>

        {/* Current time display */}
        {/* <Box textAlign="center" py={2}>
          <Text
            fontSize={{ base: '11px', md: '12px' }}
            fontWeight="700"
            textTransform="uppercase"
            color={colorMode === 'dark' ? 'orange.700' : 'blue.500'}
            mb={1}
          >
            Current Time
          </Text>
          <Text
            fontSize={{ base: '18px', md: '26px' }}
            fontWeight="700"
            color={colorMode === 'dark' ? 'white' : 'gray.800'}
          >
            {timeInSelectedZone.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
              timeZone: selectedTimezone
            })}
          </Text>
        </Box> */}


        {/* Session bars */}
        <Box mt={0} position="relative">
          <Text
            fontSize={{ base: '11px', md: '12px' }}
            fontWeight="700"
            textTransform="uppercase"
            color={colorMode === 'dark' ? 'orange.700' : 'blue.500'}
            mb={3}
          >
            Bold = Active Sessions
          </Text>
          
          <VStack spacing={3} align="stretch" position="relative">
            {sessions.map((session) => {
              const isActive = isSessionActive(session, currentHour);
              
              // Create a 24-hour representation for each session
              const renderSessionBar = () => {
                const bars = [];
                
                for (let hour = 0; hour < 24; hour++) {
                  const isSessionHour = session.start < session.end 
                    ? (hour >= session.start && hour < session.end)
                    : (hour >= session.start || hour < session.end);
                  
                  // Always use the session color, just vary the opacity
                  bars.push(
                    <Box
                      key={hour}
                      flex={1}
                      height="12px"
                      bg={session.color}
                      opacity={isSessionHour ? (isActive ? 0.9 : 0.6) : 0.2}
                      borderRight={hour < 23 ? "1px solid" : "none"}
                      borderColor={colorMode === 'dark' ? 'gray.800' : 'gray.300'}
                    />
                  );
                }
                
                return bars;
              };

              return (
                <Box key={session.name}>
                  <HStack justify="space-between" mb={1}>
                    <Text
                      fontSize="14px"
                      fontWeight={isActive ? '700' : '400'}
                      color={isActive ? (colorMode === 'dark' ? 'white' : 'gray.800') : subtitleColor}
                    >
                      {session.name}
                    </Text>
                    <Text
                      fontSize="12px"
                      color={subtitleColor}
                    >
                      {session.start === 0 ? '12am' : session.start < 12 ? `${session.start}am` : session.start === 12 ? '12pm' : `${session.start-12}pm`}
                      {' - '}
                      {session.end === 0 ? '12am' : session.end < 12 ? `${session.end}am` : session.end === 12 ? '12pm' : `${session.end-12}pm`}
                    </Text>
                  </HStack>
                  <Box position="relative">
                    <HStack spacing={0} borderRadius="sm" overflow="hidden">
                      {renderSessionBar()}
                    </HStack>
                  </Box>
                </Box>
              );
            })}
            
            {/* Global current time indicator - vertical line through all sessions */}
            <Box
              position="absolute"
              top="-30px"
              bottom="-20px"
              width="3px"
              bg={colorMode === 'dark' ? 'orange.400' : 'blue.500'}
              left={`${((currentHour + currentMinute / 60) / 24) * 100}%`}
              zIndex={10}
              boxShadow={`0 0 12px ${colorMode === 'dark' ? 'rgba(251, 211, 141, 1)' : 'rgba(66, 153, 225, 1)'}`}
              transition="left 0.5s ease-out"
            >
              {/* Playhead with time at top */}
              <Box
                position="absolute"
                top="-5px"
                left="50%"
                transform="translateX(-50%)"
                bg={colorMode === 'dark' ? 'orange.400' : 'blue.500'}
                color="#121212"
                fontSize="12px"
                fontWeight="bold"
                px={2}
                py={1}
                borderRadius="md"
                whiteSpace="nowrap"
                boxShadow="0 2px 6px rgba(0,0,0,0.3)"
              >
                {timeInSelectedZone.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </Box>
            </Box>
          </VStack>
          
          {/* Time axis labels */}
          <HStack spacing={0} mt={2}>
            {[...Array(24)].map((_, i) => (
              <Box key={i} flex={1} textAlign="center">
                {(i % 6 === 0) && (
                  <Text fontSize="10px" color={subtitleColor}>
                    {i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i-12}p`}
                  </Text>
                )}
              </Box>
            ))}
          </HStack>
        </Box>

        {/* Footer note */}
        <Flex
          justify="space-between"
          align="center"
          mt={4}
        >
          <Text
            fontSize="12px"
            color={subtitleColor}
          >
            Sessions extended for BTC markets
          </Text>
          <Select
            size="xs"
            value={selectedTimezone}
            onChange={(e) => setSelectedTimezone(e.target.value)}
            bg="transparent"
            borderColor="gray.600"
            width="auto"
          >
            {timezones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </Select>
        </Flex>
      </VStack>
    </ThemedFlex>
  );
};